import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Loader2 } from "lucide-react";

const monday = (d = new Date()) => {
  const t = new Date(d); const k = (t.getDay() + 6) % 7; t.setDate(t.getDate() - k);
  return t.toLocaleDateString("en-CA");
};

// Admin weekly report: per-user slip uploads, put-offs and average wait.
export const SlipReportDialog = ({ open, onClose }) => {
  const [week, setWeek] = useState(monday());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/packing-list/slip-report`, { headers: getAuthHeader(), params: { week_start: week } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load report"); }
    setBusy(false);
  }, [week]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const excel = async () => {
    try {
      const r = await axios.get(`${API}/packing-list/slip-report`, { headers: getAuthHeader(), params: { week_start: week, fmt: "excel" }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `SLIP-REPORT-${week}.xlsx`; a.click();
    } catch { toast.error("Download failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="slip-report-dialog">
        <DialogHeader><DialogTitle>Packing slip report — weekly</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 text-xs">
          <span>Week starting</span>
          <Input type="date" value={week} onChange={e => setWeek(monday(new Date(e.target.value)))} className="h-8 w-40 text-xs" data-testid="slip-report-week" />
          {data && <span className="text-slate-500">{data.week.start} → {data.week.end}</span>}
          <Button size="sm" variant="outline" className="ml-auto h-8" onClick={excel} data-testid="slip-report-excel">
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
        {busy ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <div className="max-h-[60vh] overflow-auto rounded border">
            <table className="w-full text-xs" data-testid="slip-report-table">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr>{["User", "Completed", "Uploaded", "≤24h", "Put off", "Pending", "Overridden", "Avg wait", "Reasons"].map(h => <th key={h} className="p-2 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(data?.users || []).length === 0 && <tr><td colSpan={9} className="p-4 text-center text-slate-500">No completed invoices this week.</td></tr>}
                {(data?.users || []).map(u => (
                  <tr key={u.email} className="border-t align-top" data-testid={`slip-report-row-${u.email}`}>
                    <td className="p-2 font-semibold">{u.user}</td>
                    <td className="p-2">{u.completed}</td><td className="p-2">{u.uploaded}</td>
                    <td className="p-2 text-emerald-700">{u.on_time}</td>
                    <td className="p-2 text-amber-700">{u.put_off}</td>
                    <td className={`p-2 ${u.pending ? "text-rose-700 font-semibold" : ""}`}>{u.pending}</td>
                    <td className="p-2">{u.overridden}</td>
                    <td className={`p-2 font-mono ${u.avg_wait_h > 24 ? "text-rose-700" : ""}`}>{u.avg_wait_h}h</td>
                    <td className="p-2 text-slate-600">{u.reasons.slice(0, 4).map(r => <div key={r}>{r}</div>)}{u.reasons.length > 4 && <div>+{u.reasons.length - 4} more</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
